import { useNavigate } from "react-router-dom";
import { useState } from "react";
import axios from "axios";
import { TIER_INFO } from "@/config/tierFeatures";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useAuth } from "@/contexts/AuthContext";
import { Check, Star, Crown, Zap, Building2, Rocket, Phone, Users, Plus, ArrowUp, Loader2, CreditCard, DollarSign, TrendingUp, Mail, MessageSquare, FileText, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Square subscription links - updated with actual plan links
const SQUARE_LINKS = {
  basic: {
    monthly: "https://checkout.square.site/merchant/MLH1TM51T0BFJ/checkout/3PNENDQAZR7FSAWX3GL6RX56",
    annual: "https://square.link/u/yS2U5xwd"
  },
  premium: {
    monthly: "https://checkout.square.site/merchant/MLH1TM51T0BFJ/checkout/V4QV3KZ6NUOOTWUC3F6RYED6",
    annual: "https://square.link/u/LTRf6CND"
  },
  deluxe: {
    monthly: "https://checkout.square.site/merchant/MLH1TM51T0BFJ/checkout/MTZ3LOIV33U3UA3BNYLZV3VU",
    annual: "https://checkout.square.site/merchant/MLH1TM51T0BFJ/checkout/MTZ3LOIV33U3UA3BNYLZV3VU"
  },
  enterprise: {
    monthly: "https://square.link/u/cXtXj1UG",
    annual: "https://square.link/u/NUmcfUCr"
  },
  upscale: {
    monthly: "https://square.link/u/rSYxrw0g",
    annual: "https://square.link/u/2ymt3kDA"
  }
};

// New pricing structure with 10% annual discount
const PLAN_DETAILS = {
  basic: {
    icon: Star,
    color: "amber",
    maxChildren: 25,
    monthlyPrice: 29,
    annualPrice: 313, // $29 x 12 = $348, 10% off = $313.20 rounded
    annualMonthly: 26, // $313 / 12 = $26.08 rounded
    savings: 35,
    features: [
      "Daily Sign-In/Out",
      "Child Management",
      "Parent & Staff Secure Document Portal",
      "Emergency Contact Report",
      "Printing & Download",
      "Smart Auto Updating Holiday & Events Calendar",
      "Smart Filters – Sort by Category / Child",
      "Local School Calendars Sync",
      "Feedback & Suggestions"
    ]
  },
  premium: {
    icon: Zap,
    color: "blue",
    maxChildren: 50,
    monthlyPrice: 49,
    annualPrice: 529, // $49 x 12 = $588, 10% off = $529.20 rounded
    annualMonthly: 44, // $529 / 12 = $44.08 rounded
    savings: 59,
    popular: true,
    includesText: "Everything in Basic PLUS:",
    features: [
      "Real Time Center Alerts to Staff in Every Room",
      "Smart Center Tour Scheduling & Live Ratio Compliance",
      "Pickup Safety Alerts (Email) option to upgrade SMS",
      "Center/Parent Communication",
      "Attendance Report with Days Present Absent & Hours Attended",
      "Daily Reports for Infants & Toddlers",
      "Attendance Trends",
      "Staff Compliance Uploads",
      "Child Compliance Uploads",
      "A.M. Bus Tracking",
      "Interactive Staff Calendar",
      "Individual Staff Assignment Report",
      "Voice Input",
      "Bulk Upload/Delete",
      "Smart 12 Month Menu Planner",
      "Lesson Plan Hub",
      "Staff Upload Center"
    ]
  },
  deluxe: {
    icon: Crown,
    color: "purple",
    maxChildren: 90,
    monthlyPrice: 79,
    annualPrice: 853, // $79 x 12 = $948, 10% off = $853.20 rounded
    annualMonthly: 71, // $853 / 12 = $71.08 rounded
    savings: 95,
    canUpscale: true,
    includesText: "Everything in Premium PLUS:",
    features: [
      "FULL COMPLIANCE DASHBOARD",
      "Inspection Mode (30 Day Window)",
      "Real time Staff/Child ratio compliance monitor",
      "Download a Printable Inspection Compliance Binder (16 items)",
      "Individual Staff & Child Missing Compliance Report Summary",
      "Smart Inspection Readiness % indicator"
    ]
  },
  enterprise: {
    icon: Building2,
    color: "emerald",
    maxChildren: 125,
    monthlyPrice: 99,
    annualPrice: 1069, // $99 x 12 = $1188, 10% off = $1069.20 rounded
    annualMonthly: 89, // $1069 / 12 = $89.08 rounded
    savings: 119,
    canUpscale: true,
    includesText: "Everything in Premium Plus PLUS:",
    features: [
      "Advanced Security Controls (IP Whitelist, MFA, Geo, Device)",
      "Assign User Roles & Permissions (Director / Administrator / Staff)",
      "Financial Management & Automation:",
      "• Invoicing",
      "• Automated Invoicing",
      "Secure Payments Powered by Square",
      "   Accept & manage credit card payments with fast, reliable processing",
      "Flexible Payment Options Including Zelle",
      "   Allow centers to offer alternative payment methods based on their preferences",
      "• Payroll Hub (Connect to Your Payroll Provider)",
      "Full Data Backup & Restore"
    ]
  }
};

const PricingPage = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { subscription, isTrialExpired, refreshSubscription } = useSubscription();
  const { user } = useAuth();
  
  // Upgrade modal state
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeCalculation, setUpgradeCalculation] = useState(null);
  const [calculatingUpgrade, setCalculatingUpgrade] = useState(false);
  const [processingUpgrade, setProcessingUpgrade] = useState(false);
  const [selectedUpgrade, setSelectedUpgrade] = useState({ plan: '', billingCycle: 'monthly' });

  // Plan order for comparison
  const PLAN_ORDER = ["basic", "premium", "deluxe", "enterprise"];
  
  const canUpgrade = (targetPlan) => {
    if (!subscription) return false;
    // Trial / expired / cancelled users haven't actually paid — let them pick ANY plan
    if (subscription.status !== 'active') return true;
    // Active paid users can only upgrade to higher tiers
    if (!subscription.plan) return false;
    const currentIndex = PLAN_ORDER.indexOf(subscription.plan);
    const targetIndex = PLAN_ORDER.indexOf(targetPlan);
    return targetIndex > currentIndex;
  };

  const handleSubscribe = (plan, billingCycle) => {
    if (!user) {
      navigate('/start-trial');
      return;
    }
    
    const link = SQUARE_LINKS[plan]?.[billingCycle];
    if (link) {
      window.open(link, '_blank');
    }
  };
  
  const handleUpgradeClick = async (plan, billingCycle) => {
    if (!user) {
      navigate('/start-trial');
      return;
    }
    
    setSelectedUpgrade({ plan, billingCycle });
    setCalculatingUpgrade(true);
    setUpgradeModalOpen(true);
    
    try {
      const response = await axios.post(`${API}/subscription/calculate-upgrade`, {
        newPlan: plan,
        newBillingCycle: billingCycle
      });
      setUpgradeCalculation(response.data);
    } catch (error) {
      console.error("Error calculating upgrade:", error);
      toast.error(error.response?.data?.detail || "Failed to calculate upgrade");
      setUpgradeModalOpen(false);
    } finally {
      setCalculatingUpgrade(false);
    }
  };
  
  const handleConfirmUpgrade = async () => {
    if (!upgradeCalculation) return;
    
    setProcessingUpgrade(true);
    
    try {
      // If there's an amount due, create dynamic Square payment link with org metadata
      if (upgradeCalculation.amountDue > 0) {
        try {
          const { data } = await axios.post(`${API}/subscription/create-upgrade-checkout`, {
            plan: selectedUpgrade.plan,
            billingCycle: selectedUpgrade.billingCycle
          });
          if (data?.payment_link_url) {
            // Store upgrade intent before redirecting
            localStorage.setItem('pendingUpgrade', JSON.stringify({
              plan: selectedUpgrade.plan,
              billingCycle: selectedUpgrade.billingCycle,
              orderId: data.order_id,
              timestamp: new Date().toISOString()
            }));
            window.location.href = data.payment_link_url;
            return;
          }
          toast.error("Could not create payment link. Please try again or contact support.");
          setProcessingUpgrade(false);
          return;
        } catch (err) {
          console.error("Error creating upgrade checkout:", err);
          toast.error(err.response?.data?.detail || "Failed to start checkout. Please try again.");
          setProcessingUpgrade(false);
          return;
        }
      }
      
      // If fully covered by credit, process upgrade directly
      const response = await axios.post(`${API}/subscription/process-upgrade`, {
        newPlan: selectedUpgrade.plan,
        newBillingCycle: selectedUpgrade.billingCycle
      });
      
      toast.success(response.data.message);
      refreshSubscription();
      setUpgradeModalOpen(false);
      navigate('/');
    } catch (error) {
      console.error("Error processing upgrade:", error);
      toast.error(error.response?.data?.detail || "Failed to process upgrade");
    } finally {
      setProcessingUpgrade(false);
    }
  };

  const handleUpscale = () => {
    const link = SQUARE_LINKS.upscale?.monthly;
    if (link) {
      window.open(link, '_blank');
    }
  };

  const handleEmailForPricing = () => {
    window.location.href = 'mailto:admin@simplesignin.com?subject=⭐ [ACTION REQUIRED] MULTI CENTER ENTERPRISE PRICING INQUIRY';
  };

  const PlanCard = ({ planKey, plan, highlighted = false }) => {
    const Icon = plan.icon;
    const colorClasses = {
      amber: "border-amber-400 bg-gradient-to-br from-amber-50 to-orange-50",
      blue: "border-blue-400 bg-gradient-to-br from-blue-50 to-cyan-50",
      purple: "border-purple-400 bg-gradient-to-br from-purple-50 to-pink-50",
      emerald: "border-emerald-400 bg-gradient-to-br from-emerald-50 to-teal-50"
    };
    const buttonClasses = {
      amber: "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600",
      blue: "bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600",
      purple: "bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600",
      emerald: "bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
    };
    const iconColorClasses = {
      amber: "text-amber-500 bg-amber-100",
      blue: "text-blue-500 bg-blue-100",
      purple: "text-purple-500 bg-purple-100",
      emerald: "text-emerald-500 bg-emerald-100"
    };

    const isCurrentPlan = subscription?.status === 'active' && subscription?.plan === planKey;

    return (
      <Card className={`relative overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${colorClasses[plan.color]} ${highlighted ? 'ring-2 ring-blue-500 shadow-lg scale-105' : 'border-2'}`}>
        {plan.popular && (
          <div className="absolute top-0 left-0 right-0">
            <Badge className="absolute -top-0 left-1/2 -translate-x-1/2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-4 py-1 rounded-b-lg rounded-t-none shadow-md">
              Most Popular
            </Badge>
          </div>
        )}
        
        <CardHeader className="text-center pt-8 pb-4">
          <div className={`mx-auto w-14 h-14 rounded-2xl flex items-center justify-center ${iconColorClasses[plan.color]} mb-3 shadow-md`}>
            <Icon className="w-7 h-7" />
          </div>
          <CardTitle className="text-2xl font-bold capitalize">{TIER_INFO[planKey]?.name || planKey}</CardTitle>
          <CardDescription className="text-gray-600 font-medium">
            Up to {plan.maxChildren} children
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Monthly Price */}
          <div className="text-center">
            <p className="text-4xl font-bold text-gray-900">
              ${plan.monthlyPrice}<span className="text-lg font-normal text-gray-500">/month</span>
            </p>
            <p className="text-sm text-gray-500 mt-1">Billed monthly</p>
          </div>

          {/* Annual Price with Discount */}
          <div className="bg-white/70 rounded-xl p-4 border border-gray-200">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Badge className="bg-green-500 text-white text-xs">Save 10%</Badge>
            </div>
            <p className="text-center text-green-600 font-bold text-lg">
              ${plan.annualMonthly}/month annual
            </p>
            <p className="text-center text-sm text-gray-600">
              Annual total: ${plan.annualPrice}
            </p>
            <p className="text-center text-xs text-green-500 font-medium">
              (Save ${plan.savings}/year)
            </p>
          </div>

          {/* Features */}
          <div className="space-y-3">
            {plan.includesText && (
              <p className="text-sm font-semibold text-gray-600 italic border-b pb-2 mb-2">
                {plan.includesText}
              </p>
            )}
            <ul className="space-y-2">
              {plan.features.map((feature, index) => (
                <li key={index} className="flex items-start gap-2">
                  <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${plan.color === 'amber' ? 'text-amber-500' : plan.color === 'blue' ? 'text-blue-500' : plan.color === 'purple' ? 'text-purple-500' : 'text-emerald-500'}`} />
                  <span className="text-sm text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Upscale Option */}
          {plan.canUpscale && (
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-3 border border-indigo-200">
              <div className="flex items-center gap-2 text-indigo-700">
                <Plus className="w-4 h-4" />
                <span className="text-sm font-medium">Add 25 children: +$15/month</span>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          {isCurrentPlan ? (
            <Button disabled className="w-full bg-green-100 text-green-700 border-2 border-green-300">
              <Check className="w-4 h-4 mr-2" />
              Current Plan
            </Button>
          ) : (subscription?.status === 'active' && !subscription?.isSubscriptionExpired) ? (
            <Button disabled className="w-full bg-gray-100 text-gray-600 border-2 border-gray-200 py-5 whitespace-normal h-auto text-sm">
              Plan changes are available after your current subscription ends. You'll keep your current plan and features until then.
            </Button>
          ) : canUpgrade(planKey) ? (
            // Trial / expired / cancelled — enter the existing recurring Square subscription checkout
            <div className="space-y-2">
              <Button 
                onClick={() => handleSubscribe(planKey, 'monthly')}
                className={`w-full ${buttonClasses[plan.color]} text-white font-semibold py-5 shadow-md`}
              >
                Choose Plan - ${plan.monthlyPrice}/mo
              </Button>
              <Button 
                onClick={() => handleSubscribe(planKey, 'annual')}
                variant="outline"
                className="w-full border-2 font-semibold py-5"
              >
                Choose Plan Annual - ${plan.annualPrice}/yr
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <Button 
                onClick={() => handleSubscribe(planKey, 'monthly')}
                className={`w-full ${buttonClasses[plan.color]} text-white font-semibold py-5 shadow-md`}
              >
                Pay Monthly - ${plan.monthlyPrice}/mo
              </Button>
              <Button 
                onClick={() => handleSubscribe(planKey, 'annual')}
                variant="outline"
                className="w-full border-2 font-semibold py-5"
              >
                Pay Annual - ${plan.annualPrice}/yr
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section with Stock Image */}
      <div className="relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url("https://images.pexels.com/photos/1001914/pexels-photo-1001914.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940")' }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-purple-900/85 to-pink-900/80" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-gray-50" />
        
        <div className="relative z-10 py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="bg-white/20 text-white backdrop-blur-sm mb-4 px-4 py-1">
              Simple, Transparent Pricing
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Choose Your Plan
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Select the perfect plan for your childcare center. All plans include unlimited staff accounts and full feature access.
            </p>
          </div>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-20 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <PlanCard planKey="basic" plan={PLAN_DETAILS.basic} />
          <PlanCard planKey="premium" plan={PLAN_DETAILS.premium} highlighted={true} />
          <PlanCard planKey="deluxe" plan={PLAN_DETAILS.deluxe} />
          <PlanCard planKey="enterprise" plan={PLAN_DETAILS.enterprise} />
        </div>

        {/* ADD-ON Section */}
        <div className="mt-8">
          <Card className="bg-gradient-to-r from-orange-500 via-rose-500 to-pink-500 border-0 text-white overflow-hidden relative">
            <CardContent className="py-4 px-4 md:px-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-xl">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="text-lg font-bold">ADD-ON</h3>
                      <Badge className="bg-white/20 text-white text-xs">Any Tier</Badge>
                      <span className="text-white font-bold text-base md:text-lg">
                        SMS Alerts Package — $14.99/mo
                      </span>
                    </div>
                    <p className="text-white/80 text-xs mt-1">
                      Instant text notifications for pickups, emergencies & daily updates
                    </p>
                  </div>
                </div>
                <div className="text-center md:text-right flex flex-col items-end gap-3">
                  <p className="text-xl font-black text-white tracking-tight">Turn Features Into Revenue</p>
                  <p className="text-sm font-bold text-white/90">Your App Can Pay for Itself</p>
                  <button
                    onClick={() => navigate('/pickup-safety#subscribe')}
                    className="mt-1 bg-white text-orange-600 font-black text-sm px-5 py-2.5 rounded-xl shadow-lg hover:bg-orange-50 transition-all hover:scale-105"
                  >
                    Subscribe — $14.99/mo →
                  </button>
                </div>
              </div>
              
              {/* Revenue Example Section */}
              <div className="mt-4 pt-4 border-t border-white/20">
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-white/10 rounded-lg p-2">
                    <DollarSign className="w-5 h-5 mx-auto mb-1 text-yellow-300" />
                    <p className="text-lg font-black text-white">$3</p>
                    <p className="text-white/80 text-xs">per child/month</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-2">
                    <Users className="w-5 h-5 mx-auto mb-1 text-yellow-300" />
                    <p className="text-lg font-black text-white">35 = $105/mo <span className="text-sm font-normal text-white/80">potential revenue</span></p>
                    <p className="text-yellow-300 font-bold text-xs">~$90/month profit</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-2">
                    <TrendingUp className="w-5 h-5 mx-auto mb-1 text-yellow-300" />
                    <p className="text-lg font-black text-white">Profit</p>
                    <p className="text-white/80 text-xs">offset your cost</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ADD-ON 2 — Parent Communication */}
        <div className="mt-6">
          <Card className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 border-0 text-white overflow-hidden relative">
            <CardContent className="py-4 px-4 md:px-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-xl">
                    <MessageSquare className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="text-lg font-bold">ADD-ON</h3>
                      <Badge className="bg-white/20 text-white text-xs">Any Tier</Badge>
                      <span className="text-white font-bold text-base md:text-lg">
                        Parent Communication — $24.99/mo or $269.89/yr (save 10%)
                      </span>
                    </div>
                    <p className="text-white/80 text-xs mt-1">
                      A complete parent portal — daily activity, messaging, documents, quick pay & more
                    </p>
                  </div>
                </div>
                <div className="text-center md:text-right flex flex-col items-end gap-3">
                  <p className="text-xl font-black text-white tracking-tight">Your App Can Pay for Itself</p>
                  <p className="text-sm font-bold text-white/90">Turn Your Parent App into a Revenue Stream</p>
                  <button
                    onClick={() => navigate('/settings/parent-communication')}
                    className="mt-1 bg-white text-emerald-700 font-black text-sm px-5 py-2.5 rounded-xl shadow-lg hover:bg-emerald-50 transition-all hover:scale-105"
                  >
                    Subscribe — $24.99/mo →
                  </button>
                </div>
              </div>

              {/* Features grid */}
              <div className="mt-4 pt-4 border-t border-white/20 grid grid-cols-2 md:grid-cols-3 gap-2 text-xs text-white/90">
                {[
                  "Parent portal login",
                  "Daily activity feed",
                  "Two-way messaging",
                  "Document sharing & uploads",
                  "Quick Pay from parents",
                  "Trial period control",
                  "Per-child billing",
                  "Auto invite emails",
                  "Webhook payment activation",
                ].map((f) => (
                  <div key={f} className="flex items-center gap-1.5">
                    <Check className="w-3.5 h-3.5 text-emerald-200 flex-shrink-0" />
                    <span>{f}</span>
                  </div>
                ))}
              </div>

              {/* Revenue Example */}
              <div className="mt-4 pt-4 border-t border-white/20">
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="bg-white/10 rounded-lg p-2">
                    <DollarSign className="w-5 h-5 mx-auto mb-1 text-yellow-300" />
                    <p className="text-lg font-black text-white">$5</p>
                    <p className="text-white/80 text-xs">per child/month</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-2">
                    <Users className="w-5 h-5 mx-auto mb-1 text-yellow-300" />
                    <p className="text-lg font-black text-white">35 = $175/mo <span className="text-sm font-normal text-white/80">potential</span></p>
                    <p className="text-yellow-300 font-bold text-xs">~$155/month profit</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-2">
                    <TrendingUp className="w-5 h-5 mx-auto mb-1 text-yellow-300" />
                    <p className="text-lg font-black text-white">Profit</p>
                    <p className="text-white/80 text-xs">offset your cost</p>
                  </div>
                </div>
              </div>
              {/* Messaging Fair Use Policy */}
              <div className="mt-5 pt-4 border-t border-white/20">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-xs text-white/90 leading-relaxed">
                  <p className="font-bold text-white mb-2 flex items-center gap-1.5">
                    <Check className="w-4 h-4 text-emerald-200" /> Messaging Fair Use Policy
                  </p>
                  <p className="mb-2">
                    Your $24.99/month plan includes generous messaging designed to easily support everyday childcare communication, such as parent updates, reminders, alerts, menus, children's activities and important notifications, etc. If your center happens to reach higher messaging levels, no worries&mdash;just reach out to us, and we'll help you continue sending messages for the rest of the month with a simple bulk pricing option.
                  </p>
                  <p>
                    This plan is built for regular day-to-day communication and isn't intended for large-scale automated marketing. If you need higher-volume messaging, we're happy to work with you&mdash;just contact us at <a href="mailto:admin@simplesignin.com" className="underline font-semibold text-emerald-200">admin@simplesignin.com</a> for options.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ADD-ON 3 — Menu, Inventory & Shopping List */}
        <div className="mt-6">
          <Card className="bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 border-0 text-white overflow-hidden relative">
            <CardContent className="py-4 px-4 md:px-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-xl">
                    <FileText className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="text-lg font-bold">ADD-ON</h3>
                      <Badge className="bg-white/20 text-white text-xs">Premium & Premium Plus</Badge>
                      <span className="text-white font-bold text-base md:text-lg">
                        Smart Menu, Inventory &amp; Shopping List — $14.99/mo
                      </span>
                    </div>
                    <p className="text-white/80 text-xs mt-1">
                      Scan your menu, take monthly inventory counts, and auto-generate shopping lists
                    </p>
                  </div>
                </div>
                <div className="text-center md:text-right flex flex-col items-end gap-3">
                  <button
                    onClick={() => navigate('/inventory-shopping-list')}
                    className="mt-1 bg-white text-orange-600 font-black text-sm px-5 py-2.5 rounded-xl shadow-lg hover:bg-orange-50 transition-all hover:scale-105"
                  >
                    Subscribe — $14.99/mo →
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Multi-Location Card */}
        <div className="mt-8">
          <Card className="bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 border-0 text-white overflow-hidden relative">
            <div className="absolute inset-0 opacity-10 pointer-events-none">
              <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
            </div>
            <CardContent className="relative py-8 px-6 md:px-12">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="p-4 bg-white/10 rounded-2xl backdrop-blur-sm">
                    <Rocket className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">Multi-Location Pricing</h3>
                    <p className="text-white/70 mt-1">
                      Managing multiple centers? We offer custom enterprise solutions with volume discounts.
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={handleEmailForPricing}
                  size="lg"
                  className="bg-white text-slate-900 hover:bg-gray-100 font-bold px-8 py-6 text-lg shadow-xl"
                >
                  <Mail className="w-5 h-5 mr-2" />
                  Email for Pricing
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Upscale Add-on Info */}
        <div className="mt-8">
          <Card className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 border-0 text-white">
            <CardContent className="py-6 px-6 md:px-12">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-white/20 rounded-xl">
                    <Users className="w-8 h-8" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold">Need More Capacity?</h3>
                    <p className="text-white/90">
                      Premium Plus & Enterprise plans can add 25 children for just <strong>$15/month</strong>
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={handleUpscale}
                  variant="secondary"
                  className="bg-white text-purple-600 hover:bg-gray-100 font-semibold px-6"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Capacity
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Back to Dashboard */}
        <div className="text-center mt-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-gray-600 hover:text-gray-900"
          >
            Back to Dashboard
          </Button>
        </div>
      </div>
      
      {/* Upgrade Modal with Proration */}
      <Dialog open={upgradeModalOpen} onOpenChange={setUpgradeModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowUp className="w-5 h-5 text-green-600" />
              Upgrade Your Plan
            </DialogTitle>
            <DialogDescription>
              Review your prorated credit and upgrade details
            </DialogDescription>
          </DialogHeader>
          
          {calculatingUpgrade ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-4" />
              <p className="text-gray-600">Calculating your upgrade...</p>
            </div>
          ) : upgradeCalculation ? (
            <div className="space-y-4">
              {/* Current vs New Plan */}
              <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
                <div className="text-center">
                  <p className="text-sm text-gray-500">Current Plan</p>
                  <p className="font-bold text-gray-700 capitalize">{upgradeCalculation.currentPlan}</p>
                </div>
                <ArrowUp className="w-6 h-6 text-green-600" />
                <div className="text-center">
                  <p className="text-sm text-gray-500">New Plan</p>
                  <p className="font-bold text-green-700 capitalize">{upgradeCalculation.newPlan}</p>
                </div>
              </div>
              
              {/* Credit Calculation */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-800 mb-2">Your Prorated Credit</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Days remaining on current plan:</span>
                    <span className="font-medium">{upgradeCalculation.daysRemaining} days</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Credit amount:</span>
                    <span className="font-medium text-green-600">-${upgradeCalculation.creditAmount.toFixed(2)}</span>
                  </div>
                </div>
              </div>
              
              {/* New Plan Pricing */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">New Plan Details</h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{upgradeCalculation.newPlan.charAt(0).toUpperCase() + upgradeCalculation.newPlan.slice(1)} ({upgradeCalculation.newBillingCycle}):</span>
                    <span className="font-medium">${upgradeCalculation.newPlanPrice.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Credit applied:</span>
                    <span className="font-medium text-green-600">-${upgradeCalculation.creditAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2 mt-2">
                    <span className="font-semibold text-gray-800">Amount Due Today:</span>
                    <span className="font-bold text-blue-700 text-lg">${upgradeCalculation.amountDue.toFixed(2)}</span>
                  </div>
                </div>
              </div>
              
              {/* New Capacity */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-purple-700">
                  <Users className="w-4 h-4" />
                  <span className="text-sm font-medium">New capacity: Up to {upgradeCalculation.maxChildren} children</span>
                </div>
              </div>
            </div>
          ) : null}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setUpgradeModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmUpgrade}
              disabled={processingUpgrade || !upgradeCalculation}
              className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
            >
              {processingUpgrade ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : upgradeCalculation?.amountDue > 0 ? (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Pay ${upgradeCalculation?.amountDue.toFixed(2)} & Upgrade
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Confirm Free Upgrade
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PricingPage;
